import { useMemo } from "react";

import { useGetProjectQuery } from "../app/services/projects";
import { useGetProjectMembershipQuery } from "../app/accounts/memberships";
import { useGetDmThreadsQuery } from "../app/services/dmThreads";
import { canCreateOffer, isSpecialistPosition } from "../utils/memberPositionUtils";

interface UseCanCreateOfferParams {
  projectId: string | undefined;
}

/**
 * 「オファー」ボタンの表示可否判定。
 *
 * - PRODUCER グループ → 常に可
 * - SPECIALIST → 自分が contractor かつ `allow_subcontracting=true` のオファーが
 *   1 件以上ある場合のみ可（受領済み / 進行中 / 完了 を含む）
 * - CLIENT グループ → プロジェクトの `is_direct_subcontract_allowed` が true のときのみ可
 */
export const useCanCreateOffer = ({
  projectId,
}: UseCanCreateOfferParams): boolean => {
  const { data: membershipData } = useGetProjectMembershipQuery(projectId ?? "", {
    skip: !projectId,
  });
  const { data: projectData } = useGetProjectQuery(
    { project_id: projectId ?? "" },
    { skip: !projectId },
  );

  const position = membershipData?.data?.position;
  const isSpecialist = !!position && isSpecialistPosition(position);

  // SPECIALIST の場合のみ「自分が contractor のスレッド一覧」を取得して
  // `allow_subcontracting=true` の有無を集計する。それ以外のロールでは
  // 余計な API を投げないために skip する。
  const { data: contractorThreadsData } = useGetDmThreadsQuery(
    { project_id: projectId ?? "", role: "contractor" },
    { skip: !projectId || !isSpecialist },
  );

  const hasReceivedAllowedSubcontract = useMemo<boolean>(() => {
    if (!isSpecialist) return false;
    const threads = contractorThreadsData?.data?.threads ?? [];
    return threads.some((t) => t.allow_subcontracting === true);
  }, [isSpecialist, contractorThreadsData]);

  return useMemo<boolean>(() => {
    if (!projectId) return false;
    return canCreateOffer(position, {
      hasReceivedAllowedSubcontract,
      isDirectSubcontractAllowed:
        projectData?.data?.project?.is_direct_subcontract_allowed ?? false,
    });
  }, [
    projectId,
    position,
    projectData,
    hasReceivedAllowedSubcontract,
  ]);
};

export default useCanCreateOffer;
